import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const TABS = [
  { key: 'upcoming', label: 'Upcoming', icon: 'fa-clock' },
  { key: 'ongoing', label: 'Ongoing', icon: 'fa-play-circle' },
  { key: 'completed', label: 'Past Events', icon: 'fa-check-circle' },
];

const STATUS_STYLES = {
  upcoming: 'bg-primary/10 text-primary border border-primary/20',
  ongoing: 'bg-amber-50 text-amber-700 border border-amber-200',
  completed: 'bg-green-50 text-green-700 border border-green-200',
};

const EventsSection = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    const tabs = getActiveTabs();
    if (tabs.length > 0 && !tabs.find(t => t.key === activeTab)) {
      setActiveTab(tabs[0].key);
    }
  }, [events]);

  const fetchEvents = async () => {
    try {
      for (const path of ['/admin/api/events.php', './admin/api/events.php']) {
        try {
          const res = await fetch(path);
          if (res.ok) {
            const data = await res.json();
            if (data.success) { setEvents(data.data); break; }
          }
        } catch { continue; }
      }
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  };

  const getFiltered = () => {
    const now = new Date();
    return events.filter(e => {
      const d = new Date(e.event_date);
      if (activeTab === 'upcoming') return d >= now && e.status === 'upcoming';
      if (activeTab === 'ongoing') return e.status === 'ongoing';
      if (activeTab === 'completed') return d < now || e.status === 'completed';
      return true;
    });
  };

  const getCount = (key) => {
    const now = new Date();
    return events.filter(e => {
      const d = new Date(e.event_date);
      if (key === 'upcoming') return d >= now && e.status === 'upcoming';
      if (key === 'ongoing') return e.status === 'ongoing';
      if (key === 'completed') return d < now || e.status === 'completed';
      return true;
    }).length;
  };

  const getActiveTabs = () => TABS.filter(t => getCount(t.key) > 0);

  const fmtDate = (s) => new Date(s).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  const fmtTime = (s) => {
    if (!s) return '';
    return new Date(`2000-01-01T${s}`).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const filtered = getFiltered();

  return (
    <section ref={ref} className="py-20 sm:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="section-badge">
            <i className="fas fa-calendar-alt" />
            School Events
          </div>
          <h2 className="section-title">Upcoming Events &amp; Activities</h2>
          <p className="section-subtitle mx-auto">Stay updated with the latest events, workshops, and activities at PAK School &amp; College System.</p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center py-20 gap-4">
            <div className="spinner" />
            <p className="text-gray-400 font-inter text-sm">Loading events...</p>
          </div>
        )}

        {!loading && (
          <>
            {/* Tabs */}
            {getActiveTabs().length > 1 && (
              <div className={`flex justify-center mb-8 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <div className="inline-flex bg-white border border-gray-200 rounded-2xl p-1.5 gap-1 shadow-sm">
                  {getActiveTabs().map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-poppins font-600 text-sm transition-all duration-200
                        ${activeTab === tab.key
                          ? 'bg-primary text-white shadow-md shadow-primary/25'
                          : 'text-gray-500 hover:text-primary hover:bg-primary/5'
                        }`}
                    >
                      <i className={`fas ${tab.icon} text-xs`} />
                      {tab.label}
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.key ? 'bg-white/20' : 'bg-gray-100'}`}>
                        {getCount(tab.key)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {filtered.length === 0 && (
              <div className="text-center py-16">
                <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-5">
                  <i className="fas fa-calendar-times text-3xl text-gray-300" />
                </div>
                <h4 className="font-poppins font-700 text-gray-700 text-lg mb-2">No Events Available</h4>
                <p className="text-gray-400 font-inter text-sm">Amazing events are coming soon. Stay tuned!</p>
              </div>
            )}

            {/* Events grid */}
            {filtered.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {filtered.map((event, idx) => (
                  <div
                    key={event.id}
                    className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 cursor-pointer transition-all duration-300 hover:-translate-y-1.5
                      ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                    style={{ transitionDelay: `${idx * 60}ms` }}
                    onClick={() => navigate(`/event-details/${event.id}`)}
                    role="article"
                    aria-label={`Event: ${event.name}`}
                  >
                    {/* Image */}
                    <div className="relative h-44 overflow-hidden">
                      {event.image_path ? (
                        <img
                          src={`https://pakschoolandcollege.com/admin/${event.image_path}`}
                          alt={event.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                          <i className="fas fa-calendar-alt text-white/50 text-4xl" />
                        </div>
                      )}
                      {/* Date badge */}
                      <div className="absolute top-3 left-3 bg-white rounded-xl px-2.5 py-1.5 text-center shadow-md">
                        <div className="font-poppins font-800 text-navy text-base leading-none">
                          {new Date(event.event_date).getDate()}
                        </div>
                        <div className="text-gray-500 text-xs leading-none mt-0.5">
                          {new Date(event.event_date).toLocaleDateString('en-US', { month: 'short' })}
                        </div>
                      </div>
                      {/* Featured */}
                      {event.featured && (
                        <div className="absolute top-3 right-3 bg-amber-400 text-navy text-xs font-poppins font-700 px-2 py-1 rounded-lg flex items-center gap-1">
                          <i className="fas fa-star text-[9px]" />
                          Featured
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <h3 className="font-poppins font-700 text-navy text-sm mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                        {event.name}
                      </h3>
                      <div className="space-y-1.5 mb-3">
                        {event.event_time && (
                          <div className="flex items-center gap-2 text-xs text-gray-400 font-inter">
                            <i className="fas fa-clock text-primary/60 w-3.5" />
                            {fmtTime(event.event_time)}
                          </div>
                        )}
                        {event.location && (
                          <div className="flex items-center gap-2 text-xs text-gray-400 font-inter">
                            <i className="fas fa-map-marker-alt text-accent/60 w-3.5" />
                            <span className="truncate">{event.location}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-poppins font-600 px-2.5 py-1 rounded-full ${STATUS_STYLES[event.status] || STATUS_STYLES.upcoming}`}>
                          {event.status?.charAt(0).toUpperCase() + event.status?.slice(1)}
                        </span>
                        <button
                          className="text-xs font-poppins font-600 text-primary hover:text-white hover:bg-primary px-3 py-1.5 rounded-lg border border-primary/30 hover:border-primary transition-all duration-200"
                          onClick={(e) => { e.stopPropagation(); navigate(`/event-details/${event.id}`); }}
                        >
                          View →
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default EventsSection;
